import validate from 'lib/utils/validationRules';

import {
  PURCHASE_TYPES,
  REFUND_ACTIVE_STATUSES,
  SAFE_DEAL_PROCESSING_STATUSES
} from 'definitions/transaction';

import {
  EXTERNAL_PAYMENT_OPTIONS,
  UZS_PAYMENT_OPTIONS
} from 'definitions/payment';

import TildaParamsService from 'services/tilda';

// Сервис для работы с объектом траназкции
const TransactionService = {

  // Если по транзакции инициирован возврат или он прошел успешно
  checkActiveStatusRefund(transaction) {
    return REFUND_ACTIVE_STATUSES.includes(transaction.status);
  },

  // Если транзакция является партнерским отчислением
  checkPartnerPayoutAccrual(transaction) {
    return transaction.type === 'PARTNER_PAYOUT_ACCRUAL';
  },

  // Если транзакция по безопасной сделки открыта или находится в процессе спора
  checkProcessingSafeDeal(transaction) {
    return SAFE_DEAL_PROCESSING_STATUSES.includes(transaction?.deal?.status);
  },

  // Если для траназкции доступен возврат
  checkRefundAvailability(transaction) {
    // Если возврат по транзакции запрещен
    if (transaction.refundBanned) {
      return false;
    }

    // Если платеж совершен с помощью внешних платежных сервисов
    if (EXTERNAL_PAYMENT_OPTIONS.includes(transaction.payment_method)) {
      return false;
    }

    // Для платежных систем узбекистана возвраты пока недоступны
    if (UZS_PAYMENT_OPTIONS.includes(transaction.payment_method)) {
      return false;
    }

    // Если это транзакция входящего платежа
    const isPurchaseType = PURCHASE_TYPES.includes(transaction.type);

    // Если по транзакции инициирован возврат или он прошел успешно
    const isRefundActive = TransactionService.checkActiveStatusRefund(transaction);

    if (isPurchaseType && isRefundActive) {
      return false;
    }

    return true;
  },

  // Сбор информации об отправителе транзакции
  getSenderContent(transaction) {
    const { senderProperties } = transaction;
    const description = senderProperties.comment || senderProperties.message || senderProperties.address;

    return {
      name: senderProperties.name,
      email: senderProperties.email,
      phone: senderProperties.phone,
      description,
    };
  },

  // Если у траназкции отсутствуют данные отправителя
  checkEmptySenderContent(transaction) {
    const senderContent = TransactionService.getSenderContent(transaction);
    const callback = (value) => validate.isNull(value);

    return validate.isObjectValuesValidByCallback(senderContent, callback);
  },

  // Сбор информации о средствах платежа
  getPaymentContent(transaction) {
    return {
      paymentMethod: transaction.payment_method,
      accountNumber: transaction.account_number,
      cardPsType: transaction.card_ps_type,
      cryptoAccrualCount: transaction.cryptoAccrualCount,
      cryptoTransactionHistoryLink: transaction.cryptoTransactionHistoryLink,
    };
  },

  // Если у транзакции есть описание или тильда парметры
  checkDescriptionOrTildaParams(transaction) {
    const { description, additional_params: additionalParams } = transaction;

    if (validate.isNotEmptyString(description)) {
      return true;
    }

    return TildaParamsService.checkTildaOrderId(additionalParams);
  },

  // Если транзакция проведена по безопасной сделки
  checkSafeDeal(transaction) {
    return validate.isObject(transaction.deal);
  },

  /*
   * Если траназкция прошла как оплата счета
   * Но если транзакция прошла как пополнение баланса проекта, то billTariffs === null
  */
  checkBillTariff(transaction) {
    const { billTariffs } = transaction;

    return validate.isNotNull(billTariffs);
  },

  // Сбор данных о тарифе по которому был проведен платеж
  getPurchaseBillTariff(transaction) {
    const { billTariffs } = transaction;
    const [billTariff] = billTariffs;

    return billTariff || null;
  },

  // Сбор данных о промокоде, примененном к транзакции
  getPromoCode(transaction) {
    const { billPromoCode } = transaction;

    if (validate.isNull(billPromoCode)) {
      return null;
    }

    return {
      amountWithDiscount: billPromoCode.amountWithDiscount,
      code: billPromoCode.code,
      currency: transaction.currency,
      discountAmount: billPromoCode.discountAmount,
      discountPercent: billPromoCode.discountPercent,
    };
  },

  // Сбор данных о варианте билета по транзакции
  getTicketVariant(transaction) {
    const { ticket_code: ticketCode } = transaction;

    if (validate.isNull(ticketCode)) {
      return null;
    }

    return {
      amount: transaction.amountPerTicket,
      code: ticketCode,
      count: transaction.ticketCount,
      currency: transaction.currency,
      link: transaction.ticket_link,
    };
  },

  // Сбор данных о варианте вознаграждения по транзакции
  getTransactionTips(transaction) {
    const { transactionTips: tips } = transaction;

    if (validate.isNull(tips)) {
      return null;
    }

    return {
      amount: tips.billAmount,
      currency: transaction.currency,
      overall: tips.overall,
      percent: tips.percent,
    };
  },

  // Сбор данных о рейтинге транзакции
  getRatingContent(transaction) {
    const { rating } = transaction;

    if (validate.isNotNumber(rating?.rating)) {
      return null;
    }

    return rating;
  },

  // Сбор данных о тарифе с минимальной суммой платежа
  getBillTariffWithMinAmount(transaction) {
    if (!TransactionService.checkBillTariff(transaction)) {
      return null;
    }

    const { billTariffs } = transaction;

    const minAmount = Math.min(
      ...billTariffs
        .map(tariff => tariff.amount)
        .filter(amount => amount)
    );

    const billTariffWithMinAmount = billTariffs
      .find(tariff => tariff.amount === minAmount);

    if (validate.isUndefined(billTariffWithMinAmount)) {
      return billTariffs[0];
    }

    return billTariffWithMinAmount;
  },

  // Проверка валюты транзакции на принадлежность к криптовалютам
  checkCryptoCurrency(transaction) {
    return transaction.currency?.type === 'CYPTO';
  },
};

export default TransactionService;
